import React, { useMemo } from 'react';
import { Form } from '../../../../../v2BaseComponents/Form';
import { useIsDisabledByParent } from '../../../../../v2BaseComponents/IsDisabledProvider';
import SideDrawerFormContainer, {
    ConfirmDirtyStateFormCloseContainer,
    SideDrawerDirtyFormExitValidationTracker,
} from '../../../../../v2BaseComponents/Form/SideDrawerFormContainer';
import useOrganizationUsers from '../../../../../../hooks/api/useOrganizationUsers';
import useOrganizationGroups from '../../../../../../hooks/api/useOrganizationGroups';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import EditOrganizationUserFields, {
    FormValue,
    formValuesToUpdateOrganizationUserArgs,
    getInitialValues,
} from '../../../../../shared/OrganizationUsers/EditOrganizationUserFields';
import { useIsOrganizationOwner } from '../../../../../../hooks/api/useOrganization';

export type EditOrganizationUserSideDrawerContentOwnProps = {
    onClose: (force: boolean) => void;
    organizationUserId: string;
};

export type EditOrganizationUserSideDrawerContentProps = EditOrganizationUserSideDrawerContentOwnProps;

const EditOrganizationUserSideDrawerContent: React.FC<EditOrganizationUserSideDrawerContentProps> = ({
    onClose,
    organizationUserId,
}) => {
    const { isLoadingUser, user, updateUser, isUpdatingUser, userGroups, isLoadingUserGroups } = useOrganizationUsers({
        organizationUserId,
        onUpdateUser: () => onClose(true),
    });
    const isOrganizationOwner = useIsOrganizationOwner();
    const { groups, isLoadingGroups } = useOrganizationGroups({});
    const isLoading = isLoadingUser || isUpdatingUser || isLoadingGroups || isLoadingGroups || isLoadingUserGroups;
    const isDisabled = useIsDisabledByParent(isLoading);

    const formValues = useMemo<FormValue>(
        () => getInitialValues({ user, userGroups, groups }),
        [groups, user, userGroups],
    );

    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <ConfirmDirtyStateFormCloseContainer>
                    <Form
                        fillContainer
                        initialValues={formValues}
                        onSubmit={(values) => {
                            updateUser(
                                formValuesToUpdateOrganizationUserArgs({
                                    organizationUserId,
                                    values,
                                    user: user!,
                                    userGroups,
                                    isUpdatingUserOrganizationOwner: isOrganizationOwner,
                                }),
                            );
                        }}
                        isDisabled={isDisabled}
                        forceStackFields
                    >
                        {({ isSubmitting, handleSubmit, values }) => {
                            return (
                                <SideDrawerFormContainer
                                    isDisabled={isDisabled || isSubmitting}
                                    onSubmit={handleSubmit}
                                    onCancel={() => onClose(true)}
                                >
                                    <SideDrawerDirtyFormExitValidationTracker />
                                    <EditOrganizationUserFields initialValues={formValues} values={values} />
                                </SideDrawerFormContainer>
                            );
                        }}
                    </Form>
                </ConfirmDirtyStateFormCloseContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(EditOrganizationUserSideDrawerContent);
